import React, { useState, useEffect } from 'react';
import '../styles/WorkflowList.css';
import { useUserType } from '../hooks/useUserType';
import { useFhirContext } from '../contexts/FhirContextProvider';


interface Task {
  id: string;
  type: number;
  workflow_id: string;
  patient_id: number;
  created_by: string;
  description: string;
  due_date: string;
  status: number;
  timestamp: string;
  is_read_only?: boolean;
  assigned_user_type?: string;
}

interface PatientInfo {
  mpi: number;
  first_name: string;
  last_name: string;
  user_type: string;
  disease_stage: string;
  vbc_program: string;
}

interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
  user_type: string;
  permissions_abac: string;
  permissions_rbac: string;
}

interface Workflow {
  id: string;
  type: number;
  patient_id: number;
  created_by: string;
  description: string;
  is_completed: boolean;
  timestamp: string;
  tasks: Task[];
  hasAccess?: boolean;
  patient?: PatientInfo | null;
  user?: UserInfo | null;
}

const TaskStatus = {
  OPEN: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
};

const WorkflowTypes = {
  1: 'TOC',
  2: 'MED_RECON',
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to check if user can manage tasks based on RBAC permissions
const canUserManageTasks = (user: UserInfo | null | undefined): boolean => {
  if (!user || !user.permissions_rbac) {
    return false;
  }
  // Check if user has "Task Manage" permission
  return user.permissions_rbac.includes('Task Manage');
};

export const WorkflowList: React.FC = () => {
  const { userId, userName, loading: userLoading } = useUserType();
  const { idToken, patient, fhirServerUrl, error: fhirError } = useFhirContext();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnlyUncompleted, setShowOnlyUncompleted] = useState(true); // Default to showing uncompleted
  const [liftWorkflowRestrictions, setLiftWorkflowRestrictions] = useState(false); // Toggle to lift workflow access restrictions

  useEffect(() => {
    if (fhirError) {
      setError(fhirError);
      setLoading(false);
      return;
    }
    
    if (idToken && patient?.id && fhirServerUrl) {
      fetchWorkflows();
    }
  }, [idToken, patient?.id, fhirServerUrl, fhirError]);

  const fetchWorkflows = async () => {
    if (!idToken) {
      setError('No ID token available');
      return;
    }

    if (!fhirServerUrl) {
      setError('No FHIR server URL available');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const workflowsResponse = await fetch(`${API_BASE_URL}/workflows/tasks?patientId=${patient?.id}`, {
        headers: {
          'X-FHIR-ID-Token': idToken,
          'X-FHIR-Server': fhirServerUrl,
          'Content-Type': 'application/json',
        },
      });
      
      if (!workflowsResponse.ok) {
        if (workflowsResponse.status === 403) {
          // User doesn't have permission - show empty workflows instead of error
          setWorkflows([]);
          setLoading(false);
          return;
        }
        throw new Error('Failed to fetch workflows');
      }
      
      const workflowsWithTasks = await workflowsResponse.json();

      // Sort workflows by timestamp - most recent first (descending)
      const sortedWorkflows = workflowsWithTasks.sort((a: Workflow, b: Workflow) => {
        const dateA = new Date(a.timestamp).getTime();
        const dateB = new Date(b.timestamp).getTime();
        return dateB - dateA; // Descending order (newest first)
      });

      setWorkflows(sortedWorkflows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkflow = (workflowId: string) => {
    setExpandedWorkflows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(workflowId)) {
        newSet.delete(workflowId);
      } else {
        newSet.add(workflowId);
      }
      return newSet;
    });
  };

  const updateTaskStatus = async (taskId: string, newStatus: number) => {
    try {
      if (newStatus === TaskStatus.COMPLETED) {
        // Use completeTask endpoint
        const response = await fetch(`${API_BASE_URL}/tasks/${taskId}/complete-ui`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-FHIR-ID-Token': idToken || '',
            'X-FHIR-Server': fhirServerUrl || '',
          },
          body: JSON.stringify({
            triggeredBy: {
              userId: userId || 'unknown@example.com',
            },
          }),
        });

        if (!response.ok) throw new Error('Failed to complete task');
      } else {
        // Update task status directly
        const response = await fetch(`${API_BASE_URL}/tasks/${taskId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-FHIR-ID-Token': idToken || '',
            'X-FHIR-Server': fhirServerUrl || '',
          },
          body: JSON.stringify({
            status: newStatus,
          }),
        });

        if (!response.ok) throw new Error('Failed to update task');
      }

      // Refresh workflows
      await fetchWorkflows();
    } catch (err) {
     
    }
  };

  const getStatusLabel = (status: number): string => {
    switch (status) {
      case TaskStatus.OPEN:
        return 'Open';
      case TaskStatus.IN_PROGRESS:
        return 'In Progress';
      case TaskStatus.COMPLETED:
        return 'Completed';
      default:
        return 'Unknown';
    }
  };

  const getStatusClass = (status: number): string => {
    switch (status) {
      case TaskStatus.OPEN:
        return 'status-open';
      case TaskStatus.IN_PROGRESS:
        return 'status-in-progress';
      case TaskStatus.COMPLETED:
        return 'status-completed';
      default:
        return '';
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter workflows based on completion status
  const filteredWorkflows = showOnlyUncompleted
    ? workflows.filter((workflow) => !workflow.is_completed)
    : workflows;

  if (loading || userLoading) {
    return (
      <div className="workflow-list-container">
        <div className="loading">
          {userLoading ? 'Loading user information...' : 'Loading workflows...'}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workflow-list-container">
        <div className="error">
          <p>Error: {error}</p>
          <button onClick={fetchWorkflows}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-list-container">
      <div className="header">
        <div className="header-left">
          <h1>Workflow Management</h1>
        </div>
        <div className="header-right">
          {userName && (
            <span className="user-info">
              {userName}
            </span>
          )}
          <button
            className={`filter-btn ${showOnlyUncompleted ? 'active' : ''}`}
            onClick={() => setShowOnlyUncompleted(!showOnlyUncompleted)}
            title={showOnlyUncompleted ? 'Show all workflows' : 'Show only uncompleted workflows'}
          >
            {showOnlyUncompleted ? '📋 Uncompleted' : '📋 All'}
          </button>
          <button
            className={`filter-btn ${liftWorkflowRestrictions ? 'active permission-filter' : ''}`}
            onClick={() => setLiftWorkflowRestrictions(!liftWorkflowRestrictions)}
            title={liftWorkflowRestrictions ? 'All workflow permissions lifted' : 'Workflow access permissions applied'}
          >
            {liftWorkflowRestrictions ? '🔓 Permissions OFF' : '🔒 Permissions ON'}
          </button>
          <button className="refresh-btn" onClick={fetchWorkflows}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {filteredWorkflows.length === 0 ? (
        <div className="no-workflows">
          <p>{showOnlyUncompleted ? 'No uncompleted workflows found' : 'No workflows found'}</p>
        </div>
      ) : (
        <div className="workflows">
          {filteredWorkflows.map((workflow) => (
            <div key={workflow.id} className={`workflow-card ${workflow.hasAccess === false && !liftWorkflowRestrictions ? 'no-access' : ''}`}>
              <div
                className="workflow-header"
                onClick={() => toggleWorkflow(workflow.id)}
              >
                <div className="workflow-info">
                  <div className="workflow-title">
                    <span className="workflow-type">
                      {WorkflowTypes[workflow.type as keyof typeof WorkflowTypes] || `Type ${workflow.type}`}
                    </span>
                    <span className="workflow-id">ID: {workflow.id}</span>
                    {workflow.hasAccess === false && !liftWorkflowRestrictions && (
                      <span className="view-only-badge">🔒 View Only</span>
                    )}
                  </div>
                  <div className="workflow-meta">
                    <span>mpi: {workflow.patient_id}</span>
                    <span>•</span>
                    <span>Created: {formatDate(workflow.timestamp)}</span>
                  </div>
                </div>
                <div className="workflow-status-container">
                  <span
                    className={`workflow-status ${
                      workflow.is_completed ? 'completed' : 'active'
                    }`}
                  >
                    {workflow.is_completed ? '✓ Completed' : '● Active'}
                  </span>
                  <span className="expand-icon">
                    {expandedWorkflows.has(workflow.id) ? '▼' : '▶'}
                  </span>
                </div>
              </div>

              {expandedWorkflows.has(workflow.id) && (
                <div className="workflow-content">
                  {workflow.patient && (
                    <div className="patient-info-section">
                      <h3>Patient Information</h3>
                      <div className="patient-details">
                        <div className="patient-detail-item">
                          <span className="detail-label">Name:</span>
                          <span className="detail-value">{workflow.patient.first_name} {workflow.patient.last_name}</span>
                        </div>
                        <div className="patient-detail-item">
                          <span className="detail-label">MPI:</span>
                          <span className="detail-value">{workflow.patient.mpi}</span>
                        </div>
                        <div className="patient-detail-item">
                          <span className="detail-label">Patient Type:</span>
                          <span className="detail-value">{workflow.patient.user_type}</span>
                        </div>
                        <div className="patient-detail-item">
                          <span className="detail-label">Disease Stage:</span>
                          <span className="detail-value">{workflow.patient.disease_stage}</span>
                        </div>
                        <div className="patient-detail-item">
                          <span className="detail-label">VBC Program:</span>
                          <span className="detail-value">{workflow.patient.vbc_program}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {workflow.user && (
                    <div className="permissions-section">
                      <h3>Your Information</h3>
                      <div className="permissions-details">
                        <div className="patient-detail-item">
                          <span className="detail-label">User Type:</span>
                          <span className="detail-value">{workflow.user.user_type}</span>
                        </div>
                        <div className="patient-detail-item">
                          <span className="detail-label">ABAC Permissions:</span>
                          <span className="detail-value">{workflow.user.permissions_abac}</span>
                        </div>
                        <div className="patient-detail-item">
                          <span className="detail-label">RBAC Permissions:</span>
                          <span className="detail-value">{workflow.user.permissions_rbac}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="tasks-section">
                    <h3>Tasks ({workflow.tasks.length})</h3>
                    {workflow.tasks.length === 0 ? (
                      <p className="no-tasks">No tasks for this workflow</p>
                    ) : (
                      <div className="tasks-list">
                        {workflow.tasks.map((task) => (
                          <div key={task.id} className="task-card">
                            <div className="task-header">
                              <div className="task-info">
                                <h4>{task.description}</h4>
                                <div className="task-meta">
                                  <span>ID: {task.id}</span>
                                  {task.due_date && (
                                    <>
                                      <span>•</span>
                                      <span>Due: {formatDate(task.due_date)}</span>
                                    </>
                                  )}
                                   {task.assigned_user_type && (
                                    <>
                                      <span>•</span>
                                      <span>Assigned to: {task.assigned_user_type}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              <div className="task-status-badge">
                                <span className={`status-badge ${getStatusClass(task.status)}`}>
                                  {getStatusLabel(task.status)}
                                </span>
                              </div>
                            </div>

                            {task.status !== TaskStatus.COMPLETED && (
                              <>
                                {!canUserManageTasks(workflow.user) || task.is_read_only || (workflow.hasAccess === false && !liftWorkflowRestrictions) ? (
                                  <div className="read-only-notice">
                                    <span>
                                      {!canUserManageTasks(workflow.user)
                                        ? '🔒 View Only - No Task Manage Permission'
                                        : workflow.hasAccess === false && !liftWorkflowRestrictions
                                        ? '🔒 View Only - No Patient Access' 
                                        : '🔒 View Only - DKC User API Task'}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="task-actions">
                                    {task.status === TaskStatus.OPEN && (
                                      <>
                                        <button
                                          className="btn btn-primary"
                                          onClick={() =>
                                            updateTaskStatus(task.id, TaskStatus.IN_PROGRESS)
                                          }
                                        >
                                          Start Task
                                        </button>
                                        <button
                                          className="btn btn-success"
                                          onClick={() =>
                                            updateTaskStatus(task.id, TaskStatus.COMPLETED)
                                          }
                                        >
                                          Complete Task
                                        </button>
                                      </>
                                    )}
                                    {task.status === TaskStatus.IN_PROGRESS && (
                                      <button
                                        className="btn btn-success"
                                        onClick={() =>
                                          updateTaskStatus(task.id, TaskStatus.COMPLETED)
                                        }
                                      >
                                        Complete Task
                                      </button>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

